import styles from "./BoxDetailsModal.module.css";
import packingGroupStyles from "@/app/fba/FbaShipmentFlow/PrepAndBoxemPage/PackingGroup.module.css";
import { useMemo, useEffect, useState, useRef} from "react";
import { FbaBox, BoxTemplate, useShipmentContext } from "@/contexts/ShipmentContext";
import Divider from '@/components/Divider/Divider';
import { Tooltip } from "@/components/Tooltip/Tooltip";
import IconTrash from "@/components/icons/IconTrash";
import { updateBoxV2, updateBoxDimensionsV2 } from "@/api/fulfillment";
import { DimensionsAndWeight, getDefaultDimensionsAndWeight } from "../utils";
import DimensionsAndWeightRow, { equals } from "../DimensionsAndWeightRow/DimensionsAndWeightRow";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "@/components/Modal/Modal";
import Flex from "@/components/Flex/Flex";
import Button from "@/components/forms/Button/Button";
import Checkbox from "@/components/forms/Checkbox/Checkbox";
import IconBox from "@/components/icons/IconBox";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import { LocalStorageKeys, setLocalStorageItem } from "@/utils/localStorageUtils";
import { CreateNewBoxButton } from "../CreateNewBoxButton/CreateNewBoxButton";
import IconArrow, { Direction } from "@/components/icons/IconArrow";
import TextInput from "@/components/forms/TextInput/TextInput";
import InputNumber from "@/components/forms/InputNumber/InputNumber";
import { API, API2 } from "@/lib/api";
import {
  RequestDTOBoxTemplateNew,
  RequestDTOBoxTemplateDelete,
  ResponseDTOBoxTemplateState,
  RequestDTOBoxTemplateSetValues,
  RequestDTOBoxTemplateMakeIDDefault,
  RequestDTOBoxTemplateMakeValueDefault,
} from "@boxem/core-types";

export interface BoxDetailsProps {
  boxes: FbaBox[];
  onClose: Function;
  open: boolean;
  createBoxes: (n: number, select?: boolean, template?: string) => Promise<void>;
  deleteBoxById: (n: number) => Promise<void>;
}

const boxToDimensionsAndWeight = (box: FbaBox): DimensionsAndWeight => {
  return {
    length: box.length,
    width: box.width,
    height: box.height,
    weight: box.weight,
  };
}

export default function BoxDetailsModal(props: BoxDetailsProps) {
  const { setCurrentBatch, currentBatch, boxTemplates, setBoxTemplatesValue, setBoxTemplatesDTO } = useShipmentContext();
  const { boxes = [], onClose, open } = props;

  const [applyToAllValue, setApplyToAllValue] = useState<DimensionsAndWeight>(getDefaultDimensionsAndWeight(boxTemplates, setBoxTemplatesDTO));
  const [loading, setLoading] = useState<boolean>(false);
  const [savingTemplate, setSavingTemplate] = useState<boolean>(false);

  const boxTemplatesToDisplay = boxTemplates.templates.filter(template => (!template.isAutoGenerated));

  const [viewingTemplates, setViewingTemplates] = useState<boolean>(boxTemplatesToDisplay.length > 0 ? true : false);
  const [savingTemplateName, setSavingTemplateName] = useState<string>("");

  const [allBoxesChecked, setAllBoxesChecked] = useState<boolean>(true);
  const [selectedBoxes, setSelectedBoxes] = useState<{[id: number]: boolean}>({});
  const [addTemplatesPerId, setAddTemplatesPerId] = useState<{ [key: string]: number }>({});

  const [ applyChangesToBoxes, setApplyChangesToBoxes ] = useState<{[key: number]: DimensionsAndWeight}>({});
  const [ applyChangesToBoxTemplates, setApplyChangesToBoxTemplates ] = useState<{[key: string]: DimensionsAndWeight}>({});

  // @NOTE(michaelp): We're going to get rid of this if we make the entirety of Prep n' Boxem
  // client-side and consolidate with a single commit state
  function localBox(box: FbaBox): FbaBox {
    let result = {...box};
    const changes = applyChangesToBoxes[box.id];
    if(changes) {
      result.length = changes.length;
      result.width = changes.width;
      result.height = changes.height;
      result.weight = changes.weight;
    }
    return result;
  }
  function localTemplate(box: BoxTemplate): BoxTemplate {
    let result = {...box};
    const changes = applyChangesToBoxTemplates[box.id];
    if(changes) {
      result.length = changes.length;
      result.width = changes.width;
      result.height = changes.height;
      result.weight = changes.weight;
    }
    return result;
  }
  

  useMemo(() => {
    for(const b of boxes) {
      selectedBoxes[b.id] = true;
    }
    setSelectedBoxes({...selectedBoxes});
  }, [open])

  useEffect(() => {
    setApplyChangesToBoxes({});
    setApplyChangesToBoxTemplates({});
  }, [open])


  const [savingDisabled, setSavingDisabled] = useState(true);

  let makeSavingDisabled = true;
  if(makeSavingDisabled) {
    for(const id in applyChangesToBoxes) {
      const ap = applyChangesToBoxes[id]
      const b = boxes.find(b => b.id?.toString() === id);
      if(b && (ap.length != b.length
            || ap.width != b.width
            || ap.height != b.height
            || ap.weight != b.weight))
      {
        makeSavingDisabled = false;
        break;
      }
    }
  }
  if(makeSavingDisabled) {
    for(const id in applyChangesToBoxTemplates) {
      const ap = applyChangesToBoxTemplates[id]
      const b = boxTemplates.templates.find(t => t.id?.toString() === id);
      if(b && (ap.length != b.length
            || ap.width != b.width
            || ap.height != b.height
            || ap.weight != b.weight))
      {
        makeSavingDisabled = false;
        break;
      }
    }
  }
  if(makeSavingDisabled) {
    for(const id in addTemplatesPerId) {
      if(addTemplatesPerId[id] > 0) {
        makeSavingDisabled = false;
        break;
      }
    }
  }
  if(makeSavingDisabled != savingDisabled) {
    setSavingDisabled(makeSavingDisabled);
  }

  return (
    <Modal
      className={styles.modal}
      closeOnOutsideClick={false}
      open={open}
      onOpenChange={open => {
        if (!open) onClose();
      }}
      growFromTop={true}
    >
      <ModalHeader>
        <strong>Box details</strong>
      </ModalHeader>

      <div style={{overflow: "auto", display: "flex", flexDirection: "column", width: "100%", flexGrow: "1" }}>
        <Flex style={{position: "relative", overflow: "visible" }} direction="column" className={styles.bulkDimsModal}>
          <Flex className={`${styles.rowHeader} ${styles.row}`}>
            <DimensionsAndWeightRow
              tabIndexesStart={10}
              showMaxWeightAndDimensionsTooltip
              value={applyToAllValue}
              setAsDefaultControls={false}
              onChange={e => setApplyToAllValue({ ...e })}
            >
              <Button
                type="secondary"
                disabled={boxes.length === 0}
                style={{alignSelf: "flex-end"}}
                onClick={() => {
                  for(const idString in selectedBoxes) {
                    if(selectedBoxes[idString]) {
                      applyChangesToBoxes[idString] = {...applyToAllValue};
                    }
                    setApplyChangesToBoxes({...applyChangesToBoxes});
                  }
                }}
                // @NOTE(michaelp): Pretty hover effect
                // onPointerEnter={() => {
                //   const elements = document.querySelectorAll('[data-apply-to-checkbox="true"]:checked');
                //   for(const _e of elements) {
                //     const e = _e as HTMLElement;
                //     e.style.transition = "all 100ms ease-out";
                //     e.style.accentColor = "var(--static-text-strong)";
                //     e.style.filter = "drop-shadow(2px 4px 6px var(--color-primary))";
                //   }
                // }}
                // onPointerLeave={() => {
                //   const elements = document.querySelectorAll('[data-apply-to-checkbox="true"]');
                //   for(const _e of elements) {
                //     const e = _e as HTMLElement;
                //     e.style.accentColor = "";
                //     e.style.filter = "";
                //   }
                // }}
              >
                Apply to selected
              </Button>
              <Button
                type="primary"
                disabled={savingTemplate}
                style={{alignSelf: "flex-end"}}
                onClick={()=> { setSavingTemplate(true); }}
              >
                Save as a template
              </Button>
            </DimensionsAndWeightRow>
          </Flex>
          {savingTemplate && <span className={styles.row} style={{paddingBottom:"6px"}}>Enter a name for your new saved box template.<span style={{color: "var(--sentiment-text-negative)"}}>*</span></span>}
          {savingTemplate && <Flex className={styles.row} style={{paddingTop:"0px"}} direction="row" gap={2} wrap={"nowrap"}>
            <TextInput tabIndex={99} value={savingTemplateName} onChange={(val)=>{setSavingTemplateName(val);}} placeholder="Box Template Name" maxLength={32} />
            <Button type="secondary" onClick={()=>{ setSavingTemplate(false); }}>Cancel</Button>
            <Button type="primary" disabled={savingTemplateName.length <= 0} onClick={async ()=>{
              let params: RequestDTOBoxTemplateNew = {
                name: savingTemplateName,
                length: applyToAllValue.length,
                width: applyToAllValue.width,
                height: applyToAllValue.height,
                weight: applyToAllValue.weight,
              };
              setSavingTemplateName("");
              API2.post<ResponseDTOBoxTemplateState>("/boxTemplates/new", params).then(setBoxTemplatesDTO)
                .catch(err => { console.log("NAY", err); });
              setSavingTemplate(false);
            }}>Save</Button>
          </Flex>}

          <Divider margin = "0"/>
          {boxes.length > 0 && (
            <Flex gap={2} align="center" className={`${styles.rowHeader} ${styles.row}`}>
              <label>
                <Checkbox
                  id="selectAll"
                  value={allBoxesChecked}
                  onChange={(e) => {
                    for(const b of boxes) {
                      selectedBoxes[b.id] = !allBoxesChecked;
                    }
                    setSelectedBoxes({...selectedBoxes});

                    setAllBoxesChecked(!allBoxesChecked);
                  }}
                />
                {allBoxesChecked ? "De-select all boxes" : "Select all boxes"}
              </label>
            </Flex>
          )}
          {boxes.sort((a,b) => { return a.displayIndex > b.displayIndex ? 1 : (a.displayIndex < b.displayIndex ? -1 : 0); }).map((boxReference, index) => {
            const box = localBox(boxReference);
            // @COPYPASTA(michaelp)(reallygoodpasta): Lots of copy pasta between BoxDetailsModal and FbaUpdateBoxDimensionsModal; low hanging fruit for consolidation
            const defaultTemplate: BoxTemplate|null = boxTemplates.templates.find(test => test.id === boxTemplates.defaultBoxTemplateId);
            const showTrash = boxes.length > 1;
            let isDefault = 
               defaultTemplate
            && defaultTemplate.isAutoGenerated
            && defaultTemplate.width == box.width
            && defaultTemplate.height == box.height
            && defaultTemplate.length == box.length
            && defaultTemplate.weight == box.weight;
            return (
            <Flex className={`${styles.row}`} key={index} align="center" gap={2}>
              <Flex align="center" gap={1} style={{whiteSpace: "nowrap"}}>
                <Checkbox
                  data-apply-to-checkbox="true"
                  value={selectedBoxes[box.id]}
                  onChange={(e) => {
                    selectedBoxes[box.id] = !selectedBoxes[box.id];
                    setSelectedBoxes({...selectedBoxes});
                  }}
                />
                <IconBox />
              </Flex>
              <div style={{display: "flex", flexDirection: "row", minWidth: "2rem", width: "6rem", flexShrink: 0.5}}>
                <h3 style={{whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>Box</h3>
                <h3 style={{whiteSpace: "nowrap", width: "2rem", minWidth: "2rem", marginLeft: "0.333em" }}>{box.displayIndex.toString()}</h3>
              </div>
              <DimensionsAndWeightRow
                tabIndexesStart={100+index*10}
                showLabels={false}
                oversizedTooltip={false}
                value={{ weight: box.weight, width: box.width, length: box.length, height: box.height }}
                isDefault={isDefault}
                makeDefault={() => {
                  // @COPYPASTA(michaelp)(templanostuff):
                  // @NOTE(michaelp): Predictive client side change based on server logic
                  // so that 3G connections are happily snappy
                  // We can make the ID whatever here, for auto-generated boxes
                  // it's only used on the client to find what values it refers to.
                  // (For example, the user can't see auto generated boxes, so they can't accidentally send requests to delete them.)
                  boxTemplates.templates = boxTemplates.templates.filter(t => !t.isAutoGenerated);
                  const temporaryBox: BoxTemplate = {
                    id: Math.random().toString(),
                    name: "Temporary client side default",
                    length: box.length,
                    width: box.width,
                    height: box.height,
                    weight: box.weight,
                    isAutoGenerated: true,
                  };
                  boxTemplates.templates.push(temporaryBox);
                  boxTemplates.defaultBoxTemplateId = temporaryBox.id;
                  setBoxTemplatesValue(boxTemplates);
                  const params: RequestDTOBoxTemplateMakeValueDefault = {
                    length: box.length,
                    width: box.width,
                    height: box.height,
                    weight: box.weight,
                  };
                  API2.post<ResponseDTOBoxTemplateState>("/boxTemplates/makeDefaultByValue", params).then(setBoxTemplatesDTO)
                    .catch(err => { console.log("Making default error", err); });
                }}
                onChange={(e) => {
                  applyChangesToBoxes[box.id] = e;
                  setApplyChangesToBoxes({...applyChangesToBoxes});
                }}
              />
              <div style={{display: "flex", pointerEvents: showTrash ? undefined : "none", opacity: showTrash ? 1 : 0}}>
                <Tooltip placement="top" title="Delete">
                  <Button type="text" onClick={()=>{props.deleteBoxById(box.id);}}>
                    <IconTrash/>
                  </Button>
                </Tooltip>
              </div>
            </Flex>);
          })}
          <Divider margin = "16px 0 0 0"/>
          <div className={`${styles.rowHeader} ${styles.row}`}>
            <Button className={styles.savedTemplatesButton} type="text" style={{height: "fit-content", minHeight: "unset", display: "flex", justifyContent: "space-between", width: "100%"}} onClick = {()=>{setViewingTemplates(!viewingTemplates);}}>
              <span style={{fontWeight: "900"}}>Saved Box Templates</span>
              <div className={styles.arrowButton} style={{marginRight: "24px"}}>
                <IconArrow size={24} direction={viewingTemplates ? Direction.Down : Direction.Up} style={{ transition: "transform 200ms" }} />
              </div>
            </Button>
          </div>
          {viewingTemplates && !boxTemplatesToDisplay.length && <div className={packingGroupStyles.emptyBoxContainer} style={{paddingBottom: "100px"}}>
            <img src="/images/isometric-open-box.svg" style={{userSelect: "none"}} height="100" />
            <h3 className={styles.emptyHeader}>You have no templates yet!</h3>
            <span className={`small-text`}>
              To make a template, scroll to the top, enter your template's values, click "Save as a template," enter a name, then click "Save"
            </span>
          </div>}
          {viewingTemplates && boxTemplatesToDisplay.map((templateOriginal, index) => {
            const template = localTemplate(templateOriginal);
            return (
              <Flex className={`${styles.row}`} key={"template" + template.id} align="center" gap={2}>
                <Tooltip placement="left" title="Create #">
                  <InputNumber tabIndex={1000 + index} value={addTemplatesPerId[template.id] ?? null} onChange={(value: number) => {
                    addTemplatesPerId[template.id] = value;
                    setAddTemplatesPerId({...addTemplatesPerId});
                  }} placeholder="0" showControls={false} wrapNumber={true} min={0} max={999} className={styles.addTemplatesInput}/>
                </Tooltip>
                <Flex align="center" gap={1} style={{whiteSpace: "nowrap"}}>
                  <IconBox />
                </Flex>
                {/*@COPYPASTA(michaelp)(asbestosisthebestos): Search tag 'asbestosisthebestos' in codebase to find duplicated code*/}
                <Tooltip onlyIfCutOff={true} delayMs={0} placement="left" title={template.name}><h3 style={{whiteSpace: "nowrap", overflow: "hidden", width: "16rem", minWidth: "1.5rem", textOverflow: "ellipsis"}}>{template.name}</h3></Tooltip>
                <DimensionsAndWeightRow
                  showLabels={false}
                  oversizedTooltip={false}
                  tabIndexesStart={2000 + index*10}
                  value={{ weight: template.weight, width: template.width, length: template.length, height: template.height }}
                  isDefault={ boxTemplates.defaultBoxTemplateId == template.id }
                  makeDefault={() => {
                    boxTemplates.defaultBoxTemplateId = template.id;
                    setBoxTemplatesValue(boxTemplates);
                    const params: RequestDTOBoxTemplateMakeIDDefault = { templateId: template.id };
                    API2.post<ResponseDTOBoxTemplateState>("/boxTemplates/makeDefault", params).then(setBoxTemplatesDTO)
                      .catch(err => { console.log("Makign template id default error, ", err); });
                  }}
                  onChange={(e) => {
                    let newApply = applyChangesToBoxTemplates;
                    newApply[template.id] = {
                      length: e.length,
                      width: e.width,
                      height: e.height,
                      weight: e.weight,
                    };
                    setApplyChangesToBoxTemplates({...newApply});
                  }}
                />
                <Tooltip placement="top" title="Delete"><div style={{display: "flex", alignItems: "center", cursor: "pointer"}} onClick={()=>{
                  const params: RequestDTOBoxTemplateDelete = { id: template.id, };
                  API2.post<ResponseDTOBoxTemplateState>("/boxTemplates/delete", params).then(setBoxTemplatesDTO)
                    .catch(err => { console.log("Deleting box template error, ", err); });
                }}><IconTrash/></div></Tooltip>
              </Flex>
            );
          })}
        </Flex>
      </div>

      <ModalFooter>
        {loading
          ? <Flex justify="end"><LoadingSpinner /></Flex>
          : <Flex gap={2} justify="space-between">
            <CreateNewBoxButton black={true} createBoxes={(n: number, templateId: string|null): Promise<void> => { return props.createBoxes(n, false, templateId); }} />

            <Flex gap={2}>
              <Button type="secondary" onClick={() => onClose()}>Cancel</Button>
              <Button
                disabled={savingDisabled}
                onClick={() => {
                  setLoading(true);

                  for(let id in addTemplatesPerId) {
                    let count = addTemplatesPerId[id];
                    if(count >= 1) props.createBoxes(count, false, id);
                    addTemplatesPerId[id] = 0;
                  }
                  setAddTemplatesPerId(addTemplatesPerId);

                  let setAnyTemplates = false;
                  for(const srcId in applyChangesToBoxTemplates) {
                    const src: DimensionsAndWeight = applyChangesToBoxTemplates[srcId];
                    const dest: BoxTemplate = boxTemplates.templates.find(t => t.id?.toString() === srcId);
                    if(dest &&
                       (dest.length != src.length
                     || dest.width != src.width
                     || dest.height != src.height
                     || dest.weight != src.weight)) {
                      dest.length = src.length;
                      dest.width = src.width;
                      dest.height = src.height;
                      dest.weight = src.weight;
                      setAnyTemplates = true;
                    }
                  }
                  if(setAnyTemplates) {
                    const params: RequestDTOBoxTemplateSetValues = [];
                    for(const t of boxTemplates.templates) {
                      params.push({
                        id: t.id,
                        length: t.length,
                        width: t.width,
                        height: t.height,
                        weight: t.weight,
                      });
                    }
                    console.log("Bulk updating them !", params);
                    API2.post<ResponseDTOBoxTemplateState>("/boxTemplates/setValues", params).then(setBoxTemplatesDTO)
                      .catch(err => { console.log("R N R ", err); });
                  }

                  const promises = [];
                  for(const id in applyChangesToBoxes) {
                    const src = applyChangesToBoxes[id];
                    const dest = currentBatch.boxes.find(b => b.id?.toString() === id);
                    if(src && dest &&
                      (src.width != dest.width
                    || src.length != dest.length
                    || src.height != dest.height
                    || src.weight != dest.weight))
                    {
                      const promise = updateBoxDimensionsV2(parseInt(id), {
                        width: src.width,
                        height: src.height,
                        length: src.length,
                        weight: src.weight,
                      }).then((result) => {
                        const dest = currentBatch.boxes.find(b => b.id?.toString() === id);
                        if(dest) {
                          dest.width = src.width;
                          dest.height = src.height;
                          dest.length = src.length;
                          dest.weight = src.weight;
                          setCurrentBatch({ ...currentBatch });
                        }
                      }).catch((reason) => {
                        console.error("Failed a box update", reason);
                      });
                      promises.push(promise);
                    }
                  }

                  Promise.all(promises).then(() => {
                    onClose();
                    setLoading(false);
                  }).catch((reason) => {
                    console.error("Failed a box update", reason);
                    onClose();
                    setLoading(false);
                  });
                }}
              >
                Save
              </Button>
            </Flex>
          </Flex>
        }
      </ModalFooter>
    </Modal>
  );
}